import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type {
  GTMEventGlycerinButtonClick,
  GTMEventGlycerinDisplayForm,
  GTMEventGlycerinDisplayOverlay,
  GTMEventGlycerinFormSubmitClick,
  GTMEventGlycerinSubmitRating,
} from '@packages/tracking/src/types/events';
import type { CreateReviewFormSchema } from '../../AddReview/AddReviewFormValidation/AddReviewFormValidation';

const defaultLabel = 'Kundenbewertungen';

export type CustomerReviewTagChipCategory = 'Vorteile' | 'Nachteile';

export const useCustomerReviewsTracking = () => {
  const dispatchTracking = useTracking();

  // Customer Reviews Form
  const trackCustomerReviewDialogOpened = () => {
    dispatchTracking<GTMEventGlycerinButtonClick>({
      event: 'ButtonClick',
      ButtonClickData: {
        category: 'Verfasse eine Bewertung',
        label: defaultLabel,
      },
    });
  };
  const trackCustomerReviewFormDisplayed = () => {
    dispatchTracking<GTMEventGlycerinDisplayForm>({
      event: 'DisplayForm',
      DisplayFormData: {
        category: 'Bewertungsformular',
        label: defaultLabel,
      },
    });
  };
  const trackCustomerReviewFormSubmitted = (
    formdata: CreateReviewFormSchema,
    articleId: GTMEventGlycerinSubmitRating['SubmitRatingData']['articleId'],
    skuId: GTMEventGlycerinSubmitRating['SubmitRatingData']['skuId'],
  ) => {
    dispatchTracking<GTMEventGlycerinFormSubmitClick>({
      event: 'FormSubmitClick',
      FormSubmitClickData: {
        category: 'Bewertungsformular',
        detail: 'Bewertung abgeben',
        label: defaultLabel,
      },
    });
    dispatchTracking<GTMEventGlycerinSubmitRating>({
      event: 'SubmitRating',
      SubmitRatingData: {
        productId: formdata.productId,
        rating: formdata.rating,
        articleId,
        skuId,
        hasTextReview: true,
      },
    });
  };
  const trackCustomerReviewButtonClick = (detail: string) => {
    dispatchTracking<GTMEventGlycerinButtonClick>({
      event: 'ButtonClick',
      ButtonClickData: {
        category: 'Bewertungsformular',
        detail,
        label: defaultLabel,
      },
    });
  };

  // RatingInfo
  const trackInfoDisplayOverlay = (label: string) => {
    dispatchTracking<GTMEventGlycerinDisplayOverlay>({
      event: 'DisplayOverlay',
      DisplayOverlayData: {
        category: 'dv-service-layer-raiting-info',
        label,
      },
    });
  };

  // CustomerReviewTerms
  const trackCustomerReviewTermsDisplayOverlay = (label: string) => {
    dispatchTracking<GTMEventGlycerinDisplayOverlay>({
      event: 'DisplayOverlay',
      DisplayOverlayData: {
        category: 'dv-service-layer-terms',
        label,
      },
    });
  };

  // CustomerRatingsShowAllCallToAction
  const trackShowAllReviewsCallToActionButtonClick = (reviewCount: string) => {
    dispatchTracking<GTMEventGlycerinButtonClick>({
      event: 'ButtonClick',
      ButtonClickData: {
        category: 'Alle Bewertungen anzeigen',
        detail: reviewCount,
        label: defaultLabel,
      },
    });
  };

  // CustomerReviewTagChip
  const trackCustomerReviewTagChipClick = (
    category: CustomerReviewTagChipCategory,
    tagName: string,
  ) => {
    dispatchTracking<GTMEventGlycerinButtonClick>({
      event: 'ButtonClick',
      ButtonClickData: {
        category,
        detail: tagName,
        label: defaultLabel,
      },
    });
  };

  return {
    trackCustomerReviewDialogOpened,
    trackCustomerReviewFormDisplayed,
    trackCustomerReviewFormSubmitted,
    trackCustomerReviewButtonClick,
    trackInfoDisplayOverlay,
    trackShowAllReviewsCallToActionButtonClick,
    trackCustomerReviewTermsDisplayOverlay,
    trackCustomerReviewTagChipClick,
  };
};
